import React, { createContext, useContext, useState } from 'react'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'
import { PopModal } from './PopModal'
import { PopFormValues } from '../types'

interface PopsModalsContextType {
  openPopModal: (opts?: OpenPopModalOpts) => void
  requestClosePopModal: () => void
}

interface OpenPopModalOpts {
  onCreated?: (data: PopFormValues) => void
  validateOnly?: boolean
  initialPopData?: PopFormValues | null
}

const PopsModalsContext = createContext<PopsModalsContextType | null>(null)

export const usePopsModals = () => {
  const ctx = useContext(PopsModalsContext)
  if (!ctx) throw new Error('usePopsModals must be used within PopsModalsProvider')
  return ctx
}

export const PopsModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('no')
  const admin_yes_cancel = useLocaleText('yes')

  const [onPopCreated, setOnPopCreated] = useState<((data: PopFormValues) => void) | null>(null)
  const [popModalOpen, setPopModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [validateOnly, setValidateOnly] = useState(false)
  const [initialPopData, setInitialPopData] = useState<PopFormValues | null>(null)

  const openPopModal = (opts?: OpenPopModalOpts) => {
    setOnPopCreated(() => opts?.onCreated || null)
    setValidateOnly(opts?.validateOnly || false)
    setInitialPopData(opts?.initialPopData || null)
    setPopModalOpen(true)
  }

  const requestClosePopModal = () => {
    setConfirmationOpen(true)
  }

  const closePopModal = () => {
    setPopModalOpen(false)
    setOnPopCreated(null)
    setValidateOnly(false)
    setInitialPopData(null)
  }

  const confirmClose = () => {
    closePopModal()
    setConfirmationOpen(false)
  }

  const cancelClose = () => setConfirmationOpen(false)

  return (
    <PopsModalsContext.Provider value={{ openPopModal, requestClosePopModal }}>
      {children}

      {popModalOpen && (
        <PopModal
          isOpen={popModalOpen}
          onClose={closePopModal}
          onRequestClose={requestClosePopModal}
          onCreated={onPopCreated || undefined}
          validateOnly={validateOnly}
          initialPopData={initialPopData}
        />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}
    </PopsModalsContext.Provider>
  )
}
